import { Component, type ErrorInfo, type ReactNode } from 'react';

export interface ErrorBoundaryFallbackProps {
  error: Error;
  /** Clears the caught error and re-renders the children. */
  onRetry: () => void;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: (props: ErrorBoundaryFallbackProps) => ReactNode;
  /** Changing this clears the error. Pass e.g. the route, so the error doesn't persist after navigating. */
  resetKey?: string;
  /** Labels the boundary in the error report, so it is obvious which part of the tree failed. */
  name: string;
}

interface ErrorBoundaryState {
  error: Error | null;
  /** The `resetKey` the current error was caught under, so a change to it can clear the error. */
  errorKey?: string;
}

/**
 * Must be a class — only class components catch render errors. Doesn't catch errors in event
 * handlers, timers or promises.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (state.error && state.errorKey !== undefined && state.errorKey !== props.resetKey) {
      return { error: null, errorKey: undefined };
    }
    return null;
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorKey: this.props.resetKey });

    // No telemetry yet; wire it up here when there is.
    console.error(`[ErrorBoundary:${this.props.name}] render failed`, error, errorInfo.componentStack);
  }

  private handleRetry = () => {
    this.setState({ error: null, errorKey: undefined });
  };

  render() {
    const { error } = this.state;
    if (error) {
      return this.props.fallback({ error, onRetry: this.handleRetry });
    }
    return this.props.children;
  }
}
